import { useNavigate } from 'react-router';
import { useAuth } from '@/utils/Auth';
import { ReactNode, useEffect } from 'react';
import { LeaveTypeProvider } from '../context/LeaveTypesContext';
import { useFirebase } from '../context/FirebaseContext';

interface Props {
  children: ReactNode;
  requiredRoles?: string[];
}

function ProtectedProviders({ children }: Props) {
  return <LeaveTypeProvider>{children}</LeaveTypeProvider>;
}

const ProtectedRoute = ({ children, requiredRoles }: Props) => {
  const { user } = useAuth();

  let firebaseContext;
  try {
    firebaseContext = useFirebase();
  } catch (error) {
    // Don't provide a default role if Firebase context fails
    // This ensures we don't accidentally grant access when context is unavailable
    firebaseContext = null;
  }

  const navigate = useNavigate();

  // If Firebase context is not available, don't allow access to protected routes
  if (!firebaseContext) {
    return null;
  }

  const { role = 'Employee' } = firebaseContext;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if route requires specific roles
    if (requiredRoles && requiredRoles.length > 0) {
      // Check if user's role matches any of the required roles
      const hasRequiredRole = requiredRoles.includes(role);

      if (!hasRequiredRole) {
        // Redirect to home page or unauthorized page if user doesn't have required role
        navigate('/'); // or navigate('/unauthorized')
      }
    }
  }, [user, role, requiredRoles, navigate]);

  if (!user) {
    return null;
  }

  // If roles are required but user's role doesn't match, don't render the protected content
  if (
    requiredRoles &&
    requiredRoles.length > 0 &&
    !requiredRoles.includes(role)
  ) {
    return null;
  }

  return <ProtectedProviders>{children}</ProtectedProviders>;
};

export default ProtectedRoute;
